<template>
  <div class="blog-page">
    <!-- Hero Banner del Blog -->
    <section class="blog-hero">
      <div class="hero-overlay"></div>
      <v-container>
        <v-row align="center" class="py-16">
          <v-col cols="12" class="text-center">
            <v-breadcrumbs :items="breadcrumbs" class="px-0 pb-4 justify-center">
              <template slot="divider">
                <v-icon>mdi-chevron-right</v-icon>
              </template>
            </v-breadcrumbs>

            <h1 class="hero-title mb-4">
              Conocimiento que <span class="highlight-text">Transforma</span>
            </h1>

            <p class="hero-subtitle mb-6">
              Insights estratégicos, casos de éxito y análisis especializados para
              potenciar tu empresa
            </p>

            <!-- Barra de búsqueda -->
            <div class="search-container">
              <v-text-field
                v-model="searchQuery"
                placeholder="Buscar artículos por palabra clave..."
                outlined
                rounded
                hide-details
                class="search-input"
                @keyup.enter="performSearch"
                @input="onSearchInput"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-magnify</v-icon>
                </template>
                <template v-slot:append>
                  <v-btn
                    color="primary"
                    dark
                    rounded
                    @click="performSearch"
                    class="search-btn"
                  >
                    Buscar
                  </v-btn>
                </template>
              </v-text-field>
            </div>

            <!-- Filtros de categorías -->
            <div class="category-filters mt-8">
              <v-chip
                v-for="category in categories"
                :key="category.id"
                :color="activeCategory === category.id ? 'primary' : 'grey lighten-3'"
                :text-color="activeCategory === category.id ? 'white' : 'grey darken-2'"
                class="category-chip mr-2 mb-2"
                @click="filterByCategory(category.id)"
              >
                <v-icon left small>{{ category.icon }}</v-icon>
                {{ category.name }}
              </v-chip>
              <v-chip
                color="grey lighten-3"
                class="category-chip mr-2 mb-2"
                @click="clearFilters"
              >
                <v-icon left small>mdi-close</v-icon>
                Todos los artículos
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Sección principal del Blog -->
    <section class="blog-main py-12">
      <v-container>
        <v-row>
          <!-- Artículos principales -->
          <v-col cols="12" lg="8">
            <!-- Indicador de resultados de búsqueda -->
            <div
              v-if="searchQuery && filteredPosts.length > 0"
              class="search-results-info mb-4"
            >
              <v-alert type="info" dense text class="mb-0">
                <div class="d-flex align-center justify-space-between">
                  <span>
                    <v-icon small left>mdi-magnify</v-icon>
                    {{ filteredPosts.length }} resultado{{
                      filteredPosts.length !== 1 ? "s" : ""
                    }}
                    para "{{ searchQuery }}"
                  </span>
                  <v-btn x-small text color="primary" @click="clearFilters">
                    <v-icon small left>mdi-close</v-icon>
                    Limpiar búsqueda
                  </v-btn>
                </div>
              </v-alert>
            </div>

            <!-- Destacado del Mes -->
            <div v-if="featuredPost && !searchQuery" class="mb-10">
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" class="mr-2">mdi-star-circle</v-icon>
                <h2 class="section-title mb-0">Artículo Destacado del Mes</h2>
              </div>
              <v-card
                class="featured-post-card"
                elevation="4"
                @click="viewPost(featuredPost.id)"
              >
                <v-img
                  :src="featuredPost.image"
                  height="400"
                  class="featured-post-img"
                  gradient="to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5)"
                >
                  <div class="featured-post-overlay pa-6 d-flex flex-column justify-end">
                    <v-chip
                      :color="getCategoryColor(featuredPost.category)"
                      dark
                      small
                      class="mb-2"
                    >
                      {{ getCategoryName(featuredPost.category) }}
                    </v-chip>
                    <h3 class="featured-post-title white--text mb-2">
                      {{ featuredPost.title }}
                    </h3>
                    <p class="featured-post-excerpt white--text mb-3">
                      {{ featuredPost.excerpt }}
                    </p>
                    <div class="featured-post-meta white--text">
                      <v-icon small color="white" class="mr-1">mdi-calendar</v-icon>
                      {{ formatDate(featuredPost.date) }}
                      <v-icon small color="white" class="ml-4 mr-1"
                        >mdi-clock-outline</v-icon
                      >
                      {{ featuredPost.readTime }} min de lectura
                      <v-icon small color="white" class="ml-4 mr-1">mdi-eye</v-icon>
                      {{ featuredPost.views.toLocaleString() }} vistas
                    </div>
                  </div>
                </v-img>
              </v-card>
            </div>

            <!-- Casos de Éxito Reales -->
            <div v-if="!searchQuery" class="mb-10">
              <div class="d-flex align-center mb-4">
                <v-icon color="success" class="mr-2">mdi-trophy</v-icon>
                <h2 class="section-title mb-0">Casos de Éxito Reales</h2>
              </div>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  v-for="caseStudy in caseStudies"
                  :key="caseStudy.id"
                >
                  <v-card
                    class="case-study-card"
                    elevation="2"
                    @click="viewCaseStudy(caseStudy.id)"
                  >
                    <v-img :src="caseStudy.image" height="200" class="case-study-img">
                      <div class="case-study-badge">
                        <v-chip color="success" dark x-small>
                          <v-icon x-small left>mdi-check-circle</v-icon>
                          Resultado Comprobado
                        </v-chip>
                      </div>
                    </v-img>

                    <v-card-text class="case-study-content">
                      <h3 class="case-study-title mb-2">{{ caseStudy.title }}</h3>
                      <div class="case-study-industry mb-2">
                        <v-icon x-small class="mr-1">mdi-domain</v-icon>
                        {{ caseStudy.industry }}
                      </div>

                      <div class="case-study-results mb-3">
                        <div
                          v-for="result in caseStudy.keyResults"
                          :key="result"
                          class="result-item"
                        >
                          <v-icon x-small color="success" class="mr-1">mdi-check</v-icon>
                          {{ result }}
                        </div>
                      </div>

                      <div class="case-study-footer">
                        <div class="client-logo">
                          <v-img :src="caseStudy.clientLogo" height="30" contain></v-img>
                        </div>
                        <v-btn
                          color="primary"
                          small
                          text
                          @click.stop="openWhatsAppCaseStudy(caseStudy.id)"
                        >
                          <v-icon small left>mdi-whatsapp</v-icon>
                          Quiero Esto
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Guías Prácticas -->
            <div v-if="!searchQuery" class="mb-8">
              <div class="d-flex align-center mb-4">
                <v-icon color="info" class="mr-2">mdi-book-open-variant</v-icon>
                <h2 class="section-title mb-0">Guías y Tutoriales Prácticos</h2>
              </div>

              <v-row>
                <v-col cols="12" md="6" v-for="guide in practicalGuides" :key="guide.id">
                  <v-card class="guide-card" elevation="2" @click="viewPost(guide.id)">
                    <div
                      class="guide-icon"
                      :style="{
                        backgroundColor: getCategoryColor(guide.category) + '20',
                      }"
                    >
                      <v-icon :color="getCategoryColor(guide.category)" large>{{
                        guide.icon
                      }}</v-icon>
                    </div>
                    <v-card-text class="guide-content">
                      <h3 class="guide-title mb-2">{{ guide.title }}</h3>
                      <p class="guide-description mb-3">{{ guide.description }}</p>

                      <div class="guide-difficulty mb-3">
                        <span class="difficulty-label">Nivel:</span>
                        <v-rating
                          :value="guide.difficulty"
                          color="amber"
                          dense
                          small
                          readonly
                          class="ml-2"
                        ></v-rating>
                        <span class="difficulty-text ml-2">{{
                          getDifficultyText(guide.difficulty)
                        }}</span>
                      </div>

                      <div class="guide-steps">
                        <div class="step-count">
                          <v-icon small class="mr-1">mdi-format-list-numbered</v-icon>
                          {{ guide.steps }} pasos
                        </div>
                        <div class="time-required ml-4">
                          <v-icon small class="mr-1">mdi-timer-outline</v-icon>
                          {{ guide.timeRequired }}
                        </div>
                      </div>

                      <v-btn
                        color="primary"
                        small
                        text
                        class="mt-3"
                        @click.stop="openWhatsAppGuide(guide.id)"
                      >
                        <v-icon small left>mdi-help-circle</v-icon>
                        ¿Necesitas ayuda?
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Lista de artículos -->
            <div class="mb-8">
              <div class="d-flex justify-space-between align-center mb-6">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-newspaper-variant</v-icon>
                  <h2 class="section-title mb-0">
                    {{ searchQuery ? "Resultados de Búsqueda" : "Artículos Recientes" }}
                  </h2>
                </div>
                <div class="sort-select">
                  <v-select
                    v-model="sortBy"
                    :items="sortOptions"
                    label="Ordenar por"
                    outlined
                    dense
                    hide-details
                    class="sort-select-input"
                  ></v-select>
                </div>
              </div>

              <!-- Mensaje cuando no hay resultados -->
              <div v-if="filteredPosts.length === 0" class="no-results text-center py-12">
                <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-magnify</v-icon>
                <h3 class="mb-3">No se encontraron artículos</h3>
                <p class="mb-6">
                  Intenta con otras palabras clave o explora nuestras categorías
                </p>
                <v-btn color="primary" @click="clearFilters">
                  <v-icon left>mdi-close</v-icon>
                  Limpiar búsqueda
                </v-btn>
              </div>

              <!-- Lista de artículos -->
              <v-row v-else>
                <v-col cols="12" v-for="post in paginatedPosts" :key="post.id">
                  <v-card
                    class="post-card-large"
                    elevation="2"
                    @click="viewPost(post.id)"
                  >
                    <v-row no-gutters>
                      <v-col cols="12" md="4">
                        <v-img
                          :src="post.image"
                          height="200"
                          class="post-img-large"
                        ></v-img>
                      </v-col>
                      <v-col cols="12" md="8">
                        <v-card-text class="post-content-large">
                          <div class="post-category mb-2">
                            <v-chip :color="getCategoryColor(post.category)" dark x-small>
                              {{ getCategoryName(post.category) }}
                            </v-chip>
                          </div>

                          <h3 class="post-title-large mb-2">{{ post.title }}</h3>
                          <p class="post-excerpt-large mb-3">{{ post.excerpt }}</p>

                          <div class="post-meta-large mb-3">
                            <div class="author-info">
                              <v-avatar size="32" class="mr-2">
                                <img :src="post.author.avatar" :alt="post.author.name" />
                              </v-avatar>
                              <div>
                                <div class="author-name">{{ post.author.name }}</div>
                                <div class="author-role">{{ post.author.role }}</div>
                              </div>
                            </div>
                            <div class="post-stats">
                              <div class="stat-item">
                                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                {{ formatDate(post.date) }}
                              </div>
                              <div class="stat-item ml-4">
                                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                                {{ post.readTime }} min
                              </div>
                              <div class="stat-item ml-4">
                                <v-icon x-small class="mr-1">mdi-comment</v-icon>
                                {{ post.comments }}
                              </div>
                            </div>
                          </div>

                          <div class="post-tags">
                            <v-chip
                              v-for="tag in post.tags.slice(0, 3)"
                              :key="tag"
                              x-small
                              outlined
                              class="mr-1 mb-1"
                            >
                              {{ tag }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Paginación -->
              <div v-if="filteredPosts.length > 0" class="text-center mt-8">
                <v-pagination
                  v-model="currentPage"
                  :length="totalPages"
                  :total-visible="7"
                  circle
                  color="primary"
                ></v-pagination>
              </div>
            </div>
          </v-col>

          <!-- Sidebar -->
          <v-col cols="12" lg="4">
            <div class="blog-sidebar">
              <!-- CTA Consultoría Gratuita -->
              <v-card class="sidebar-card cta-card mb-6" elevation="4">
                <v-card-text class="text-center pa-6">
                  <v-icon size="64" color="primary" class="mb-4">mdi-headset</v-icon>
                  <h3 class="sidebar-title mb-3">¿Necesitas Asesoría Especializada?</h3>
                  <p class="sidebar-text mb-4">
                    Agenda una consultoría gratuita de 30 minutos con nuestros expertos
                  </p>
                  <v-btn
                    color="primary"
                    large
                    block
                    @click="openWhatsAppGeneral"
                    class="mb-3"
                  >
                    <v-icon left>mdi-calendar-clock</v-icon>
                    Agendar Consultoría
                  </v-btn>
                  <v-btn color="green darken-2" dark block @click="openWhatsAppGeneral">
                    <v-icon left>mdi-whatsapp</v-icon>
                    Chat Inmediato
                  </v-btn>
                </v-card-text>
              </v-card>

              <!-- eBook Gratuito -->
              <v-card class="sidebar-card ebook-card mb-6" elevation="2">
                <v-card-text class="text-center pa-6">
                  <div class="ebook-cover mb-4">
                    <v-icon size="80" color="primary">mdi-book-open-page-variant</v-icon>
                  </div>
                  <h3 class="sidebar-title mb-3">Ebook Gratuito</h3>
                  <p class="sidebar-text mb-3">
                    "Transformación Digital: 10 Estrategias Comprobadas"
                  </p>
                  <v-form @submit.prevent="downloadEbook">
                    <v-text-field
                      v-model="ebookEmail"
                      placeholder="Tu correo"
                      type="email"
                      outlined
                      dense
                      hide-details
                      class="mb-3"
                      required
                    ></v-text-field>
                    <v-btn color="primary" block type="submit" :loading="ebookLoading">
                      <v-icon left>mdi-download</v-icon>
                      Descargar Ebook
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>

              <!-- Categorías con Estadísticas -->
              <v-card class="sidebar-card mb-6" elevation="2">
                <v-card-title class="sidebar-header">
                  <v-icon left>mdi-folder-multiple</v-icon>
                  Temas Especializados
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-list dense>
                    <v-list-item
                      v-for="category in categories"
                      :key="category.id"
                      @click="filterByCategory(category.id)"
                      :class="{ 'active-category': activeCategory === category.id }"
                    >
                      <v-list-item-icon>
                        <v-icon small :color="category.color">{{ category.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ category.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{
                          category.description
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip x-small>{{ category.count }}</v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>

              <!-- Expertos del Equipo -->
              <v-card class="sidebar-card mb-6" elevation="2">
                <v-card-title class="sidebar-header">
                  <v-icon left>mdi-account-group</v-icon>
                  Nuestros Expertos
                </v-card-title>
                <v-card-text class="pa-4">
                  <div
                    v-for="expert in teamExperts"
                    :key="expert.id"
                    class="expert-item mb-3"
                    @click="viewExpertPosts(expert.id)"
                  >
                    <div class="d-flex align-center">
                      <v-avatar size="48" class="mr-3">
                        <img :src="expert.avatar" :alt="expert.name" />
                      </v-avatar>
                      <div>
                        <h4 class="expert-name mb-1">{{ expert.name }}</h4>
                        <p class="expert-role mb-1">{{ expert.role }}</p>
                        <div class="expert-specialty">
                          <v-chip
                            v-for="spec in expert.specialties.slice(0, 2)"
                            :key="spec"
                            x-small
                            class="mr-1"
                          >
                            {{ spec }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Testimonios de Clientes -->
              <v-card class="sidebar-card mb-6" elevation="2">
                <v-card-title class="sidebar-header">
                  <v-icon left>mdi-comment-quote</v-icon>
                  Lo que Dicen Nuestros Clientes
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-carousel
                    height="200"
                    hide-delimiters
                    cycle
                    interval="5000"
                    class="testimonials-carousel"
                  >
                    <v-carousel-item
                      v-for="(testimonial, index) in testimonials"
                      :key="index"
                    >
                      <div class="testimonial-item text-center pa-4">
                        <v-icon color="amber" class="mb-2">mdi-format-quote-open</v-icon>
                        <p class="testimonial-text mb-3">{{ testimonial.text }}</p>
                        <div class="testimonial-author">
                          <strong>{{ testimonial.name }}</strong>
                          <div class="testimonial-company">{{ testimonial.company }}</div>
                        </div>
                      </div>
                    </v-carousel-item>
                  </v-carousel>
                </v-card-text>
              </v-card>

              <!-- Webinars Próximos -->
              <v-card class="sidebar-card" elevation="2">
                <v-card-title class="sidebar-header">
                  <v-icon left>mdi-video</v-icon>
                  Próximos Webinars
                </v-card-title>
                <v-card-text class="pa-4">
                  <div
                    v-for="webinar in upcomingWebinars"
                    :key="webinar.id"
                    class="webinar-item mb-4"
                  >
                    <div class="d-flex align-start">
                      <div class="webinar-date mr-3">
                        <div class="webinar-day">{{ webinar.day }}</div>
                        <div class="webinar-month">{{ webinar.month }}</div>
                      </div>
                      <div>
                        <h4 class="webinar-title mb-1">{{ webinar.title }}</h4>
                        <p class="webinar-details mb-1">
                          <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                          {{ webinar.time }}
                        </p>
                        <p class="webinar-details mb-2">
                          <v-icon x-small class="mr-1">mdi-account</v-icon>
                          {{ webinar.expert }}
                        </p>
                        <v-btn
                          color="primary"
                          x-small
                          @click="registerWebinar(webinar.id)"
                        >
                          Inscribirme Gratis
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Sección de Recursos Descargables -->
    <section
      v-if="!searchQuery"
      class="resources-section py-12"
      style="background: #f8fafc"
    >
      <v-container>
        <div class="text-center mb-8">
          <h2 class="section-title mb-4">Recursos Exclusivos</h2>
          <p class="section-subtitle">
            Herramientas y plantillas para impulsar tu empresa
          </p>
        </div>

        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="resource in resources" :key="resource.id">
            <v-card
              class="resource-card"
              elevation="2"
              @click="downloadResource(resource.id)"
            >
              <div class="resource-header">
                <div
                  class="resource-icon"
                  :style="{ backgroundColor: resource.color + '20' }"
                >
                  <v-icon size="48" :color="resource.color">{{ resource.icon }}</v-icon>
                </div>
                <v-chip
                  v-if="resource.premium"
                  color="amber"
                  dark
                  x-small
                  class="premium-chip"
                >
                  Premium
                </v-chip>
              </div>
              <v-card-text class="text-center pa-4">
                <h4 class="resource-title mb-2">{{ resource.title }}</h4>
                <p class="resource-description mb-3">{{ resource.description }}</p>
                <div class="resource-stats">
                  <div class="stat">
                    <v-icon x-small class="mr-1">mdi-download</v-icon>
                    {{ resource.downloads }} descargas
                  </div>
                  <div class="stat ml-3">
                    <v-icon x-small class="mr-1">mdi-star</v-icon>
                    {{ resource.rating }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Sección de Métricas de Impacto -->
    <section v-if="!searchQuery" class="metrics-section py-12">
      <v-container>
        <div class="text-center mb-8">
          <h2 class="section-title mb-4">Impacto Demostrado</h2>
          <p class="section-subtitle">
            Resultados reales que generamos para nuestros clientes
          </p>
        </div>

        <v-row>
          <v-col cols="6" md="3" v-for="metric in impactMetrics" :key="metric.id">
            <div class="metric-card text-center">
              <div class="metric-icon mb-3">
                <v-icon size="48" :color="metric.color">{{ metric.icon }}</v-icon>
              </div>
              <div class="metric-value">{{ metric.value }}</div>
              <div class="metric-label">{{ metric.label }}</div>
              <div class="metric-description">{{ metric.description }}</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Final -->
    <section v-if="!searchQuery" class="cta-section py-12">
      <v-container>
        <v-card class="cta-card-final" color="primary" dark>
          <v-row align="center" no-gutters>
            <v-col cols="12" md="8" class="pa-8">
              <h2 class="cta-title mb-3">¿Listo para Transformar tu Empresa?</h2>
              <p class="cta-text mb-0">
                Contáctanos hoy mismo y descubre cómo podemos ayudarte a alcanzar tus
                objetivos empresariales.
              </p>
            </v-col>
            <v-col cols="12" md="4" class="text-center pa-8">
              <v-btn color="white" large @click="openWhatsAppGeneral" class="cta-btn">
                <v-icon left>mdi-whatsapp</v-icon>
                Contactar Ahora
              </v-btn>
              <p class="cta-subtext mt-3">Respuesta en menos de 15 minutos</p>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </section>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" top>
      <div class="d-flex align-center">
        <v-icon class="mr-3">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "BlogPage",
  data() {
    return {
      breadcrumbs: [
        { text: "Inicio", disabled: false, href: "/" },
        { text: "Blog", disabled: true },
      ],
      searchQuery: "",
      activeCategory: null,
      sortBy: "recent",
      currentPage: 1,
      itemsPerPage: 4,
      ebookEmail: "",
      ebookLoading: false,

      categories: [
        {
          id: 1,
          name: "Casos de Éxito",
          icon: "mdi-trophy",
          color: "amber",
          description: "Resultados comprobados de clientes",
          count: 24,
        },
        {
          id: 2,
          name: "Tecnología Empresarial",
          icon: "mdi-laptop",
          color: "blue",
          description: "Soluciones para empresas",
          count: 18,
        },
        {
          id: 3,
          name: "Transformación Digital",
          icon: "mdi-chart-timeline",
          color: "purple",
          description: "Estrategias de digitalización",
          count: 15,
        },
        {
          id: 4,
          name: "Automatización",
          icon: "mdi-robot",
          color: "green",
          description: "Procesos automatizados",
          count: 12,
        },
        {
          id: 5,
          name: "Outsourcing IT",
          icon: "mdi-account-multiple",
          color: "teal",
          description: "Talento especializado",
          count: 9,
        },
        {
          id: 6,
          name: "Soporte Técnico",
          icon: "mdi-headset",
          color: "orange",
          description: "Soporte 24/7",
          count: 7,
        },
        {
          id: 7,
          name: "Formación Corporativa",
          icon: "mdi-school",
          color: "red",
          description: "Capacitación especializada",
          count: 11,
        },
        {
          id: 8,
          name: "Consultoría Estratégica",
          icon: "mdi-chart-box",
          color: "indigo",
          description: "Asesoría especializada",
          count: 13,
        },
      ],

      sortOptions: [
        { text: "Más recientes", value: "recent" },
        { text: "Más populares", value: "popular" },
        { text: "Más comentados", value: "commented" },
        { text: "Más tiempo de lectura", value: "longest" },
      ],

      // Artículo destacado del mes
      featuredPost: {
        id: 101,
        title: "Cómo una PYME logró aumentar sus ventas en 300% con automatización",
        excerpt:
          "Caso real de implementación de CRM y automatización de marketing que revolucionó los resultados de una empresa familiar.",
        content: "...",
        image:
          "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
        category: 1,
        tags: ["Automatización", "PYMES", "CRM", "Marketing", "Resultados"],
        date: "2024-01-20",
        readTime: 12,
        views: 5240,
        comments: 89,
        author: {
          id: 1,
          name: "Carlos Mendoza",
          avatar:
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
          role: "Director de Proyectos",
        },
      },

      // Casos de éxito
      caseStudies: [
        {
          id: 201,
          title: "Empresa de retail optimiza inventarios con IA",
          excerpt: "Reducción de 40% en costos de almacenamiento",
          industry: "Retail",
          image:
            "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          clientLogo:
            "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
          keyResults: [
            "40% reducción costos almacenamiento",
            "25% aumento rotación inventario",
            "99.8% precisión en predicciones",
          ],
          duration: "3 meses",
          roi: "280%",
        },
        {
          id: 202,
          title: "Hospital implementa sistema de telemedicina",
          excerpt: "Atención remota para 5,000 pacientes mensuales",
          industry: "Salud",
          image:
            "https://images.unsplash.com/photo-1586773860418-dc22f8b874bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          clientLogo:
            "https://images.unsplash.com/photo-1516549655669-df4f6a6f8d64?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
          keyResults: [
            "5,000 pacientes atendidos remotamente",
            "60% reducción tiempo de espera",
            "95% satisfacción pacientes",
          ],
          duration: "4 meses",
          roi: "320%",
        },
      ],

      // Guías prácticas
      practicalGuides: [
        {
          id: 301,
          title: "Guía completa para elegir CRM empresarial",
          description: "10 criterios clave para seleccionar el CRM ideal",
          category: 2,
          icon: "mdi-account-box",
          difficulty: 2,
          steps: 8,
          timeRequired: "15 min lectura",
        },
        {
          id: 302,
          title: "Checklist de seguridad informática",
          description: "Protege tu empresa de ciberataques",
          category: 6,
          icon: "mdi-shield-check",
          difficulty: 3,
          steps: 12,
          timeRequired: "20 min lectura",
        },
      ],

      // Artículos normales (copia original)
      allPosts: [
        {
          id: 1,
          title: "5 Errores Comunes en la Transformación Digital y Cómo Evitarlos",
          excerpt:
            "Análisis de los principales errores que cometen las empresas al iniciar su proceso de digitalización.",
          content: "...",
          image:
            "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          category: 3,
          tags: ["Transformación Digital", "Errores", "Mejores Prácticas"],
          date: "2024-01-18",
          readTime: 8,
          views: 3245,
          comments: 56,
          author: {
            id: 2,
            name: "Ana García",
            avatar:
              "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
            role: "Consultora Senior",
          },
        },
        {
          id: 2,
          title: "Outsourcing IT: ¿Cuándo es la Mejor Opción para tu Empresa?",
          excerpt:
            "Guía completa para decidir si externalizar tu departamento de TI es la mejor decisión.",
          content: "...",
          image:
            "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          category: 5,
          tags: ["Outsourcing", "TI", "Costos", "Eficiencia"],
          date: "2024-01-15",
          readTime: 10,
          views: 2876,
          comments: 42,
          author: {
            id: 3,
            name: "Roberto Silva",
            avatar:
              "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
            role: "Especialista en TI",
          },
        },
        {
          id: 3,
          title: "Automatización de Procesos: De la Teoría a la Práctica",
          excerpt:
            "Implementación paso a paso de sistemas RPA en procesos empresariales.",
          content: "...",
          image:
            "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          category: 4,
          tags: ["RPA", "Automatización", "Procesos", "Eficiencia"],
          date: "2024-01-12",
          readTime: 12,
          views: 4123,
          comments: 78,
          author: {
            id: 4,
            name: "Laura Martínez",
            avatar:
              "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
            role: "Ingeniera de Procesos",
          },
        },
        {
          id: 4,
          title: "Soporte Técnico 24/7: Por qué es Esencial Hoy en Día",
          excerpt:
            "Análisis del impacto del soporte continuo en la productividad empresarial.",
          content: "...",
          image:
            "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          category: 6,
          tags: ["Soporte Técnico", "Disponibilidad", "Productividad"],
          date: "2024-01-10",
          readTime: 7,
          views: 1987,
          comments: 34,
          author: {
            id: 5,
            name: "Miguel Torres",
            avatar:
              "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
            role: "Director de Soporte",
          },
        },
        {
          id: 5,
          title: "Inteligencia Artificial en la Gestión Empresarial",
          excerpt: "Cómo implementar soluciones de IA para optimizar procesos internos.",
          content: "...",
          image:
            "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
          category: 2,
          tags: ["IA", "Gestión", "Optimización", "Tecnología"],
          date: "2024-01-08",
          readTime: 11,
          views: 3567,
          comments: 45,
          author: {
            id: 1,
            name: "Carlos Mendoza",
            avatar:
              "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
            role: "Director de Proyectos",
          },
        },
      ],

      // Posts filtrados (se inicializa con todos los posts)
      posts: [],

      // Expertos del equipo
      teamExperts: [
        {
          id: 1,
          name: "Carlos Mendoza",
          role: "Director de Proyectos",
          avatar:
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
          specialties: ["Transformación Digital", "Automatización", "CRM"],
          articles: 24,
          experience: 12,
        },
        {
          id: 2,
          name: "Ana García",
          role: "Consultora Senior",
          avatar:
            "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
          specialties: ["Estrategia Empresarial", "Marketing Digital", "Analytics"],
          articles: 18,
          experience: 8,
        },
        {
          id: 3,
          name: "Roberto Silva",
          role: "Especialista en TI",
          avatar:
            "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80",
          specialties: ["Infraestructura IT", "Ciberseguridad", "Cloud"],
          articles: 15,
          experience: 10,
        },
      ],

      // Testimonios
      testimonials: [
        {
          text:
            "Gracias a Innova A&G automatizamos procesos que nos ahorran 40 horas semanales de trabajo manual.",
          name: "Juan Pérez",
          company: "Distribuidora Alimentos S.A.",
        },
        {
          text:
            "Su soporte técnico 24/7 nos permitió reducir el downtime de nuestros sistemas en un 95%.",
          name: "María López",
          company: "Clínica Santa María",
        },
        {
          text:
            "La capacitación de su equipo transformó completamente nuestras habilidades tecnológicas.",
          name: "Carlos Rodríguez",
          company: "Constructora Andina",
        },
      ],

      // Webinars
      upcomingWebinars: [
        {
          id: 1,
          title: "Automatización Inteligente para PYMES",
          day: "25",
          month: "ENE",
          time: "16:00 - 17:30",
          expert: "Carlos Mendoza",
        },
        {
          id: 2,
          title: "Seguridad Informática Básica",
          day: "28",
          month: "ENE",
          time: "11:00 - 12:30",
          expert: "Roberto Silva",
        },
      ],

      // Recursos
      resources: [
        {
          id: 1,
          title: "Plantilla Plan Digital",
          description: "Plan estratégico de transformación digital",
          icon: "mdi-file-chart",
          color: "primary",
          downloads: "1.2k",
          rating: "4.8",
          premium: false,
        },
        {
          id: 2,
          title: "Calculadora ROI",
          description: "Calcula el retorno de inversión de proyectos TI",
          icon: "mdi-calculator",
          color: "success",
          downloads: "890",
          rating: "4.9",
          premium: true,
        },
        {
          id: 3,
          title: "Checklist CRM",
          description: "Lista para elegir el mejor CRM",
          icon: "mdi-checkbox-marked",
          color: "info",
          downloads: "1.5k",
          rating: "4.7",
          premium: false,
        },
        {
          id: 4,
          title: "Guía Contratos TI",
          description: "Modelos de contratos para servicios",
          icon: "mdi-file-document",
          color: "warning",
          downloads: "720",
          rating: "4.6",
          premium: true,
        },
      ],

      // Métricas de impacto
      impactMetrics: [
        {
          id: 1,
          value: "300%",
          label: "Incremento Ventas",
          description: "Promedio en clientes PYMES",
          icon: "mdi-chart-line",
          color: "success",
        },
        {
          id: 2,
          value: "40h",
          label: "Horas Ahorradas",
          description: "Semanalmente por cliente",
          icon: "mdi-timer-sand",
          color: "primary",
        },
        {
          id: 3,
          value: "95%",
          label: "Satisfacción",
          description: "Clientes satisfechos",
          icon: "mdi-emoticon-happy",
          color: "amber",
        },
        {
          id: 4,
          value: "280%",
          label: "ROI Promedio",
          description: "Retorno de inversión",
          icon: "mdi-cash-multiple",
          color: "green",
        },
      ],

      snackbar: {
        show: false,
        message: "",
        color: "",
        icon: "",
      },

      whatsappNumber: "51917915824",
      searchTimeout: null,
    };
  },

  computed: {
    filteredPosts() {
      let posts = [...this.posts];

      // Filtrar por categoría
      if (this.activeCategory) {
        posts = posts.filter((post) => post.category === this.activeCategory);
      }

      // Ordenar
      switch (this.sortBy) {
        case "recent":
          posts.sort((a, b) => new Date(b.date) - new Date(a.date));
          break;
        case "popular":
          posts.sort((a, b) => b.views - a.views);
          break;
        case "commented":
          posts.sort((a, b) => b.comments - a.comments);
          break;
        case "longest":
          posts.sort((a, b) => b.readTime - a.readTime);
          break;
      }

      return posts;
    },

    paginatedPosts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPosts.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredPosts.length / this.itemsPerPage);
    },
  },

  mounted() {
    // Inicializar los posts con todos los artículos
    this.posts = [...this.allPosts];
  },

  methods: {
    // Método de búsqueda principal
    performSearch() {
      this.currentPage = 1;

      if (!this.searchQuery.trim()) {
        // Si la búsqueda está vacía, mostrar todos los posts
        this.posts = [...this.allPosts];
        this.showSnackbar("Mostrando todos los artículos", "info", "mdi-information");
        return;
      }

      const query = this.searchQuery.toLowerCase().trim();

      // Filtrar posts
      this.posts = this.allPosts.filter((post) => {
        // Buscar en título
        const titleMatch = post.title.toLowerCase().includes(query);

        // Buscar en extracto
        const excerptMatch = post.excerpt.toLowerCase().includes(query);

        // Buscar en tags
        const tagsMatch = post.tags.some((tag) => tag.toLowerCase().includes(query));

        // Buscar en contenido del autor
        const authorMatch =
          post.author.name.toLowerCase().includes(query) ||
          post.author.role.toLowerCase().includes(query);

        // Buscar en nombre de categoría
        const categoryMatch = this.getCategoryName(post.category)
          .toLowerCase()
          .includes(query);

        return titleMatch || excerptMatch || tagsMatch || authorMatch || categoryMatch;
      });

      // Mostrar mensaje con resultados
      if (this.posts.length === 0) {
        this.showSnackbar(
          `No se encontraron artículos para "${this.searchQuery}"`,
          "info",
          "mdi-information"
        );
      } else {
        this.showSnackbar(
          `Encontrados ${this.posts.length} artículo${
            this.posts.length !== 1 ? "s" : ""
          }`,
          "success",
          "mdi-check"
        );
      }
    },

    // Búsqueda en tiempo real con debounce
    onSearchInput() {
      // Limpiar timeout anterior
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Si el campo está vacío, limpiar búsqueda inmediatamente
      if (!this.searchQuery.trim()) {
        this.posts = [...this.allPosts];
        return;
      }

      // Esperar 500ms después de la última tecla para hacer la búsqueda
      this.searchTimeout = setTimeout(() => {
        if (this.searchQuery.length >= 2) {
          this.performSearch();
        }
      }, 500);
    },

    // Filtro por categoría
    filterByCategory(categoryId) {
      // Primero aplicar búsqueda si hay
      if (this.searchQuery) {
        this.performSearch();
      } else {
        this.posts = [...this.allPosts];
      }

      // Aplicar filtro de categoría
      this.activeCategory = this.activeCategory === categoryId ? null : categoryId;
      this.currentPage = 1;

      if (this.activeCategory) {
        const category = this.categories.find((c) => c.id === categoryId);
        this.showSnackbar(
          `Mostrando artículos de ${category.name}`,
          "info",
          category.icon
        );
      }
    },

    // Limpiar todos los filtros
    clearFilters() {
      this.activeCategory = null;
      this.searchQuery = "";
      this.currentPage = 1;
      this.posts = [...this.allPosts];
      this.showSnackbar(
        "Filtros limpiados. Mostrando todos los artículos",
        "success",
        "mdi-check"
      );
    },

    // Métodos de navegación
    viewPost(postId) {
      this.$router.push(`/blog/${postId}`);
    },

    viewCaseStudy(caseId) {
      this.$router.push(`/casos-exito/${caseId}`);
    },

    viewExpertPosts(expertId) {
      this.$router.push(`/blog/autor/${expertId}`);
    },

    // Métodos de utilidad
    getCategoryColor(categoryId) {
      const category = this.categories.find((c) => c.id === categoryId);
      return category ? category.color : "grey";
    },

    getCategoryName(categoryId) {
      const category = this.categories.find((c) => c.id === categoryId);
      return category ? category.name : "General";
    },

    formatDate(dateString) {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString("es-ES", options);
    },

    getDifficultyText(difficulty) {
      switch (difficulty) {
        case 1:
          return "Básico";
        case 2:
          return "Intermedio";
        case 3:
          return "Avanzado";
        default:
          return "Básico";
      }
    },

    // Métodos de WhatsApp
    openWhatsApp(message) {
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${this.whatsappNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, "_blank");
    },

    openWhatsAppGeneral() {
      const message = `Hola, soy [Tu Nombre]. Me interesa una consultoría gratuita:

• Empresa: 
• Sector: 
• Necesidad específica: 
• Mejor horario para contactarme: 

Por favor coordinar una reunión.`;
      this.openWhatsApp(message);
    },

    openWhatsAppCaseStudy(caseId) {
      const caseStudy = this.caseStudies.find((c) => c.id === caseId);
      const message = `Hola, me interesa el caso de éxito "${caseStudy?.title}"

• Mi empresa: 
• Sector: 
• Necesidad similar: 
• Presupuesto aproximado: 

Solicito reunión para analizar mi caso.`;
      this.openWhatsApp(message);
    },

    openWhatsAppGuide(guideId) {
      const guide = this.practicalGuides.find((g) => g.id === guideId);
      const message = `Hola, necesito ayuda con: "${guide?.title}"

• Duda específica: 
• Mi nivel actual: 
• Tiempo disponible: 
• Contacto preferido: 

Gracias.`;
      this.openWhatsApp(message);
    },

    // Métodos de descarga y suscripción
    downloadEbook() {
      if (!this.ebookEmail) {
        this.showSnackbar("Por favor ingresa tu correo", "error", "mdi-alert");
        return;
      }

      this.ebookLoading = true;

      // Simular descarga
      setTimeout(() => {
        this.showSnackbar(
          "¡Ebook enviado a tu correo! Revisa tu bandeja de entrada.",
          "success",
          "mdi-email-check"
        );
        this.ebookEmail = "";
        this.ebookLoading = false;
      }, 1500);
    },

    downloadResource(resourceId) {
      const resource = this.resources.find((r) => r.id === resourceId);

      if (resource.premium) {
        const message = `Hola, quiero descargar el recurso premium: "${resource.title}"

• Nombre: 
• Correo: 
• Empresa: 

Por favor enviar acceso.`;
        this.openWhatsApp(message);
      } else {
        // Simular descarga gratuita
        this.showSnackbar(
          `¡${resource.title} descargado exitosamente!`,
          "success",
          "mdi-download"
        );
      }
    },

    registerWebinar(webinarId) {
      const webinar = this.upcomingWebinars.find((w) => w.id === webinarId);
      const message = `Hola, quiero registrarme al webinar: "${webinar?.title}"

• Nombre completo: 
• Correo electrónico: 
• Empresa: 
• Cargo: 

Confirmar registro por favor.`;
      this.openWhatsApp(message);
    },

    registerEvent(eventId) {
      const event = this.upcomingWebinars.find((e) => e.id === eventId);
      const message = `Hola, me registro al evento: "${event?.title}"

• Nombre: 
• Correo: 
• Teléfono: 
• Preguntas específicas: 

Gracias.`;
      this.openWhatsApp(message);
    },

    // Notificaciones
    showSnackbar(message, color, icon) {
      this.snackbar = {
        show: true,
        message,
        color,
        icon,
      };
    },
  },

  watch: {
    // Resetear página cuando cambian los filtros
    searchQuery(newVal) {
      if (!newVal) {
        this.posts = [...this.allPosts];
      }
    },

    activeCategory() {
      this.currentPage = 1;
    },

    sortBy() {
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.blog-page {
  background: #f8fafc;
}

/* Hero Section */
.blog-hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;
}

.highlight-text {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.5rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto 2rem;
}

.search-container {
  max-width: 600px;
  margin: 0 auto;
}

.search-input {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50px !important;
}

.search-btn {
  border-radius: 25px !important;
  font-weight: 600;
}

.category-filters {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.category-chip {
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.category-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Resultados de búsqueda */
.search-results-info {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* No resultados */
.no-results {
  background: white;
  border-radius: 12px;
  padding: 48px 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.no-results h3 {
  color: #64748b;
  font-size: 1.5rem;
  margin-bottom: 12px;
}

.no-results p {
  color: #94a3b8;
  max-width: 400px;
  margin: 0 auto;
}

/* Featured Post */
.featured-post-card {
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.featured-post-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
}

.featured-post-img {
  border-radius: 16px;
}

.featured-post-overlay {
  height: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
}

.featured-post-title {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
}

.featured-post-excerpt {
  font-size: 1.1rem;
  opacity: 0.9;
  line-height: 1.5;
}

.featured-post-meta {
  font-size: 0.9rem;
  opacity: 0.8;
}

/* Case Studies */
.case-study-card {
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
}

.case-study-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.case-study-img {
  position: relative;
}

.case-study-badge {
  position: absolute;
  top: 12px;
  right: 12px;
}

.case-study-content {
  padding: 20px;
}

.case-study-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.case-study-industry {
  font-size: 0.875rem;
  color: #64748b;
  display: flex;
  align-items: center;
}

.case-study-results {
  margin: 12px 0;
}

.result-item {
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.case-study-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.client-logo {
  width: 100px;
}

/* Guides */
.guide-card {
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
}

.guide-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.guide-icon {
  text-align: center;
  padding: 30px;
  border-bottom: 1px solid #e2e8f0;
}

.guide-content {
  padding: 20px;
}

.guide-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.guide-description {
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.5;
}

.guide-difficulty {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #475569;
}

.difficulty-label {
  font-weight: 600;
}

.difficulty-text {
  font-size: 0.8rem;
  color: #64748b;
}

.guide-steps {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #64748b;
}

/* Post Cards Large */
.post-card-large {
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.post-card-large:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.post-img-large {
  height: 100%;
  min-height: 200px;
}

.post-content-large {
  padding: 24px;
  height: 100%;
}

.post-title-large {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
  margin-bottom: 12px;
}

.post-excerpt-large {
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 16px;
}

.post-meta-large {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
}

.author-role {
  font-size: 0.8rem;
  color: #64748b;
}

.post-stats {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #64748b;
}

.stat-item {
  display: flex;
  align-items: center;
}

.post-tags {
  display: flex;
  flex-wrap: wrap;
}

/* Sidebar */
.blog-sidebar {
  position: sticky;
  top: 20px;
}

.sidebar-card {
  border-radius: 12px;
  overflow: hidden;
}

.cta-card {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.cta-card .sidebar-title {
  color: white;
}

.cta-card .sidebar-text {
  color: rgba(255, 255, 255, 0.9);
}

.ebook-card {
  border: 2px solid #3b82f6;
}

.ebook-cover {
  background: linear-gradient(135deg, #3b82f620 0%, #1d4ed820 100%);
  border-radius: 8px;
  padding: 20px;
}

.sidebar-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  color: #1e293b;
}

.active-category {
  background-color: #f1f5f9 !important;
  border-left: 3px solid #3b82f6;
}

.expert-item {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 12px;
  border-radius: 8px;
}

.expert-item:hover {
  background-color: #f8fafc;
}

.expert-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  margin: 0;
}

.expert-role {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0 0 8px 0;
}

.testimonials-carousel {
  border-radius: 8px;
  overflow: hidden;
}

.testimonial-item {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.testimonial-text {
  font-style: italic;
  color: #475569;
  line-height: 1.6;
}

.testimonial-author {
  color: #1e293b;
}

.testimonial-company {
  font-size: 0.85rem;
  color: #64748b;
}

.webinar-item {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.webinar-item:hover {
  border-color: #3b82f6;
  background-color: #f8fafc;
}

.webinar-date {
  text-align: center;
  min-width: 60px;
}

.webinar-day {
  font-size: 1.5rem;
  font-weight: 700;
  color: #3b82f6;
}

.webinar-month {
  font-size: 0.8rem;
  color: #64748b;
  text-transform: uppercase;
}

.webinar-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  margin: 0 0 4px 0;
}

.webinar-details {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Resources Section */
.resource-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
}

.resource-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.resource-header {
  position: relative;
  padding: 24px 24px 0 24px;
}

.resource-icon {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-bottom: 16px;
}

.premium-chip {
  position: absolute;
  top: 12px;
  right: 12px;
}

.resource-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 1.1rem;
  margin-bottom: 8px;
}

.resource-description {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 12px;
}

.resource-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Metrics Section */
.metrics-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.metric-card {
  padding: 30px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  display: inline-block;
  padding: 16px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
}

.metric-value {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin: 16px 0 8px;
}

.metric-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}

.metric-description {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.4;
}

/* CTA Final */
.cta-section {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

.cta-card-final {
  border-radius: 20px;
  overflow: hidden;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: white;
}

.cta-text {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
}

.cta-btn {
  border-radius: 12px !important;
  font-weight: 600;
  padding: 0 32px !important;
  height: 56px !important;
  color: #1e293b !important;
}

.cta-subtext {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.25rem;
  }

  .featured-post-title {
    font-size: 1.75rem;
  }

  .post-title-large {
    font-size: 1.25rem;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .featured-post-title {
    font-size: 1.5rem;
  }

  .category-chip {
    font-size: 0.8rem;
    margin: 4px;
  }

  .post-meta-large {
    flex-direction: column;
    align-items: flex-start;
  }

  .post-stats {
    margin-top: 12px;
    flex-wrap: wrap;
  }

  .stat-item {
    margin-right: 16px;
    margin-bottom: 8px;
  }

  .metric-value {
    font-size: 2rem;
  }

  .cta-title {
    font-size: 2rem;
  }

  .cta-text {
    font-size: 1.1rem;
  }
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.featured-post-card,
.case-study-card,
.guide-card,
.post-card-large,
.resource-card,
.metric-card {
  animation: fadeIn 0.6s ease-out;
}

/* Estados hover mejorados */
.category-chip,
.case-study-card,
.guide-card,
.post-card-large,
.resource-card,
.expert-item,
.webinar-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Mejoras de accesibilidad */
:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Scroll suave */
html {
  scroll-behavior: smooth;
}

/* Mejoras de tipografía */
.section-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1.5rem;
  position: relative;
}

.section-title::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto 3rem;
}

/* Efecto de búsqueda activa */
.search-input.v-input--is-focused {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  transition: box-shadow 0.3s ease;
}
</style>
